import { yupResolver } from '@hookform/resolvers/yup';
import { AdminUpdateQuotasRequest, AdminUpdateQuotasResponse, Organisation } from '@avanzu/types';
import { Button, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { FormContainer, SwitchElement, TextFieldElement } from 'react-hook-form-mui';
import * as yup from 'yup';
import { apiClient } from '../../../services/apiClient';

interface OrganisationQuotaEditorProps {
  organisation: Organisation;
  onUpdate: (organisation: Organisation) => void;
}

interface QuotaFormData {
  quotaMaxProjectsPerYear: number;
  quotaMaxTransactionsPerProject: number;
  quotaMaxUsers: number;
  quotaAiEnabled: boolean;
}

const schema = yup.object({
  quotaMaxProjectsPerYear: yup
    .number()
    .required('Required')
    .integer('Must be an integer')
    .min(-1, 'Must be -1 or greater'),
  quotaMaxTransactionsPerProject: yup
    .number()
    .required('Required')
    .integer('Must be an integer')
    .min(-1, 'Must be -1 or greater'),
  quotaMaxUsers: yup
    .number()
    .required('Required')
    .integer('Must be an integer')
    .min(-1, 'Must be -1 or greater'),
  quotaAiEnabled: yup.boolean().required(),
});

export default function OrganisationQuotaEditor({
  organisation,
  onUpdate,
}: OrganisationQuotaEditorProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  const onSubmit = async (data: QuotaFormData) => {
    setLoading(true);

    try {
      const response = await apiClient.put(
        `/admin/organisations/${organisation.id}/quotas`,
        data as AdminUpdateQuotasRequest
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update quotas');
      }

      const result: AdminUpdateQuotasResponse = await response.json();
      onUpdate(result.organisation);
      enqueueSnackbar('Quotas updated successfully', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to update quotas', {
        variant: 'error',
      });
      console.error('Error updating quotas:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Organisation Quotas
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manually override quota limits for this organisation. Use -1 for unlimited.
        </Typography>

        <FormContainer
          defaultValues={{
            quotaMaxProjectsPerYear: organisation.quotaMaxProjectsPerYear,
            quotaMaxTransactionsPerProject: organisation.quotaMaxTransactionsPerProject,
            quotaMaxUsers: organisation.quotaMaxUsers,
            quotaAiEnabled: organisation.quotaAiEnabled,
          }}
          resolver={yupResolver(schema)}
          onSuccess={onSubmit}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <TextFieldElement
                name="quotaMaxProjectsPerYear"
                label="Max Projects Per Year"
                type="number"
                fullWidth
                helperText="Use -1 for unlimited"
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextFieldElement
                name="quotaMaxTransactionsPerProject"
                label="Max Transactions Per Project"
                type="number"
                fullWidth
                helperText="Use -1 for unlimited"
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextFieldElement
                name="quotaMaxUsers"
                label="Max Users"
                type="number"
                fullWidth
                helperText="Use -1 for unlimited"
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <SwitchElement
                name="quotaAiEnabled"
                label="AI Features Enabled"
                switchProps={{ disabled: loading }}
                sx={{ mt: 1 }}
              />
            </Grid>
          </Grid>

          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>
        </FormContainer>
      </CardContent>
    </Card>
  );
}
